import React, { useState } from 'react';

import ModalContainer from '@components/modals/ModalContainer';
import ModalHeader from '@components/modals/ModalHeader';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import {
	useAddMaterialRequisitionPreliminaryItemsMutation,
	useFetchFabricTypeListQuery,
	useFetchMaterialPreliminaryItemsQuery
} from '@features/material-requisition/api/materialRequisitionApi';
import Composition from '@features/material-requisition/components/Composition';
import { renderAsyncDropdown } from '@features/material-requisition/components/index';
import {
	generateFabricPreliminaryItem,
	MATERIAL_REQUISITION_PURPOSE,
	validateFabricPreliminaryValidation
} from '@features/material-requisition/utils';
import NitexDevFabricTable from '@features/material-requisition/components/NitexDevFabricTable';
import MaterialPreliminaryItemTable from '@features/material-requisition/components/MaterialPreliminaryItemTable';

import { validateNumber } from '@utils/Util';

const ViewMaterialRequisition = ({ onCloseModal, selectedRequisition, setSelectedRequisition }) => {
	const { data: materials, isLoading } = useFetchMaterialPreliminaryItemsQuery(
		{ requisitionId: selectedRequisition?.id },
		{ skip: !selectedRequisition?.id }
	);
	const { data: fabricTypes } = useFetchFabricTypeListQuery();
	const [selectedFabricType, setSelectedFabricType] = useState(null);
	const [isShowMaterialForm, setIsShowMaterialForm] = useState(false);
	const [addMaterialRequisitionPreliminaryItems, { isLoading: isLoadingPreliminaryAdd }] =
		useAddMaterialRequisitionPreliminaryItemsMutation();
	const [inputData, setInputData] = useState({});
	const [errors, setErrors] = useState({});

	const renderMaterialsTable = () => {
		if (isNitexDevelopmentFabric()) {
			return (
				<NitexDevFabricTable
					materials={materials}
					setIsShowMaterialForm={setIsShowMaterialForm}
					selectedRequisition={selectedRequisition}
				/>
			);
		} else {
			return (
				<MaterialPreliminaryItemTable
					materials={materials}
					setIsShowMaterialForm={setIsShowMaterialForm}
					selectedRequisition={selectedRequisition}
				/>
			);
		}
	};

	const fabricPromiseOptions = (inputValue) => {
		return new Promise(async (resolve) => {
			const data = fabricTypes?.filter((item) =>
				item.label.toLowerCase().includes(inputValue.toLowerCase())
			);
			resolve(data);
		});
	};

	const onChangeHandler = (e) => {
		const { name, value } = e.target;
		setInputData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleCompositions = (compositions) => {
		setInputData((prev) => ({
			...prev,
			compositions: compositions
		}));
	};

	const renderRequisitionForms = () => {
		if (isNitexDevelopmentFabric() && isShowMaterialForm) {
			return (
				<>
					<div className="row">
						{renderAsyncDropdown(
							'Fabric Type *',
							fabricPromiseOptions,
							(items) => setSelectedFabricType(items),
							selectedFabricType,
							'Select',
							false,
							fabricTypes,
							errors?.fabricTypeError
						)}
						<div className="col-md-6">
							<div className="form-group">
								<label htmlFor="gsm">GSM</label>
								<input
									className="input-percentage"
									max={100}
									min="0.1"
									type="number"
									name="gsm"
									value={inputData.gsm || ''}
									onChange={onChangeHandler}
								/>
								{errors?.gsmError && <p className="error d-block">{errors?.gsmError}</p>}
							</div>
						</div>
						<div className="col-md-6">
							<div className="form-group composition-item weight-item mb-0">
								<label htmlFor="quantity">Required quantity</label>
								<div className="d-flex">
									<input
										className="input-percentage"
										max={100}
										min="0.1"
										type="number"
										name="quantity"
										onKeyPress={validateNumber}
										value={inputData.quantity || ''}
										onChange={onChangeHandler}
									/>
									<div className="select-component">
										<div className="form-group size36">
											<div className="select-component">
												<div className="click-box click mb-1">
													<p className="regular-14 ">
														{selectedFabricType?.label?.startsWith('Knit')
															? 'Kg'
															: selectedFabricType?.label
																? 'Yard'
																: ''}
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
								{errors?.quantityError && (
									<p className="error d-block">{errors?.quantityError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6">
							<div className="form-group cost-group">
								<label htmlFor="price">Cost (BDT)</label>
								<div className="with-icon right-icon">
									<input
										className="input-percentage"
										max={100}
										min="0.1"
										type="number"
										name="price"
										value={inputData.price || ''}
										onChange={onChangeHandler}
									/>
									<span className="right-icon border-left h-100 text-center">
										<span className="gray-dark-02 regular-14">৳</span>
									</span>
								</div>
								{errors?.priceError && <p className="error d-block">{errors?.priceError}</p>}
							</div>
						</div>
					</div>
					<div className="row repeat">
						<Composition addCompositions={handleCompositions} errors={errors} materialIndex={0} />
					</div>
				</>
			);
		}
	};

	const isNitexDevelopmentFabric = () => {
		return (
			[MATERIAL_REQUISITION_PURPOSE[0].constant, MATERIAL_REQUISITION_PURPOSE[2].constant].includes(
				selectedRequisition?.purpose
			) && selectedRequisition?.requisitionType === 'FABRIC'
		);
	};

	const handleSubmit = async () => {
		const validationErrors = validateFabricPreliminaryValidation(inputData, selectedFabricType);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		setErrors({});
		try {
			const requestBody = {
				requisitionId: selectedRequisition?.id,
				...generateFabricPreliminaryItem(inputData, selectedFabricType)
			};
			const { data } = await addMaterialRequisitionPreliminaryItems({ requestBody });
			setSelectedRequisition(data.materialRequisitionResponse);
			toastSuccess('Material added successfully');
			setIsShowMaterialForm(false);
			setInputData({});
			setSelectedFabricType(null);
		} catch {
			toastError('Failed to add material');
		}
	};

	const isShowAddBtn = () => {
		return ['REQUESTED', 'MATERIAL_ADDED'].includes(selectedRequisition.status);
	};

	return (
		<LoaderComponent loading={isLoading || isLoadingPreliminaryAdd}>
			<ModalContainer className="buyer-request-details-modal material-requirement-modal pr-0">
				<div className="popup-wrapper">
					<div className="common-popup">
						<ModalHeader
							title="Add material(s) that is closest to the requirement"
							onClose={onCloseModal}
						/>
						<div className="common-popup-body body-scroll">
							{isNitexDevelopmentFabric() && materials?.length === 0 && !isShowMaterialForm && (
								<div className="empty-section pt-4">
									<div className="empty-section-content d-flex justify-content-center align-items-center">
										<p className="regular-14 gray-dark-02 mr-2">No material added.</p>
										{isShowAddBtn() && (
											<button
												className="button size32"
												onClick={() => setIsShowMaterialForm(true)}
											>
												Add Material
											</button>
										)}
									</div>
								</div>
							)}
							{materials?.length > 0 && renderMaterialsTable()}
							{renderRequisitionForms()}
						</div>

						{isShowMaterialForm && (
							<div className="modal-footer">
								<div className="footer-buttons text-right">
									<button className="button outline size32 ml-2" onClick={onCloseModal}>
										Cancel
									</button>
									<button
										className="button size32 ml-2"
										disabled={isLoadingPreliminaryAdd}
										onClick={handleSubmit}
									>
										Submit
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ViewMaterialRequisition;
